import { lazy, Suspense } from 'react';
import { RoleGuard, RoleSwitch } from '@emfood/shared';
import { Outlet, Navigate, createRoute, SearchSchemaInput } from '@tanstack/react-router';

import { ROLES } from 'src/api/roles-config';
import DashboardLayout from 'src/layouts/dashboard';

import AppAuthGuard from 'src/components/auth/app-auth-guard';
import { SplashScreen, LoadingScreen } from 'src/components/loading-screen';

import { MeasurementsListSearchParamsSchema } from 'src/sections/markers/measurements-list/search-params';
import { MeasuringMethodsListSearchParamsSchema } from 'src/sections/markers/measuring-methods-list/search-params';
import { MeasuringMethodRequestsListSearchParamsSchema } from 'src/sections/markers/measuring-method-requests-list/search-params';

import { rootRoute } from './root';

const ProducerMarkersList = lazy(() => import('src/sections/markers/producer-marker-list/view'));
const MarkersList = lazy(() => import('src/sections/markers/markers-list/view'));
const MeasuringMethodsList = lazy(() => import('src/sections/markers/measuring-methods-list/view'));
const ProducerMeasuringMethodsList = lazy(
  () => import('src/sections/markers/producer-measuring-methods-list/view')
);
const MeasuringMethodRequestList = lazy(
  () => import('src/sections/markers/measuring-method-requests-list/view')
);
const MeasurementSubmissionsList = lazy(
  () => import('src/sections/markers/measurements-list/view')
);

const ProducerMeasurementSubmissionsList = lazy(
  () => import('src/sections/markers/producer-measurements-list/view')
);

// Markers routes
const markersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'markers',
  component: () => (
    <AppAuthGuard splashScreen={<SplashScreen />}>
      <DashboardLayout>
        <Suspense fallback={<LoadingScreen />}>
          <Outlet />
        </Suspense>
      </DashboardLayout>
    </AppAuthGuard>
  ),
});

const markersIndexRoute = createRoute({
  getParentRoute: () => markersRoute,
  path: '/',
  component: () => (
    <RoleSwitch
      options={[
        { roles: [ROLES.ADMIN], component: <MarkersList /> },
        { roles: [ROLES.PRODUCER_ADMIN], component: <ProducerMarkersList /> },
      ]}
      fallback={<Navigate to="/403" replace />}
    />
  ),
});

const markersMeasuringMethodsRoute = createRoute({
  getParentRoute: () => markersRoute,
  path: '/measuring-methods',
  validateSearch: (search: unknown & SearchSchemaInput) =>
    MeasuringMethodsListSearchParamsSchema.validateSync(search),
  component: () => (
    <RoleSwitch
      options={[
        { roles: [ROLES.ADMIN], component: <MeasuringMethodsList /> },
        { roles: [ROLES.PRODUCER_ADMIN], component: <ProducerMeasuringMethodsList /> },
      ]}
      fallback={<Navigate to="/403" replace />}
    />
  ),
});

const markersMeasuringMethodRequestsRoute = createRoute({
  getParentRoute: () => markersRoute,
  path: '/measuring-methods/requests',
  validateSearch: (search: unknown & SearchSchemaInput) =>
    MeasuringMethodRequestsListSearchParamsSchema.validateSync(search),
  component: () => (
    <RoleGuard requiredRoles={[ROLES.ADMIN]}>
      <MeasuringMethodRequestList />
    </RoleGuard>
  ),
});

const markersMeasurementSubmissionsRoute = createRoute({
  getParentRoute: () => markersRoute,
  path: '/measurements',
  validateSearch: (search: unknown & SearchSchemaInput) =>
    MeasurementsListSearchParamsSchema.validateSync(search),
  component: () => (
    <RoleSwitch
      options={[
        { roles: [ROLES.ADMIN], component: <MeasurementSubmissionsList /> },
        { roles: [ROLES.PRODUCER_ADMIN], component: <ProducerMeasurementSubmissionsList /> },
      ]}
      fallback={<Navigate to="/403" replace />}
    />
  ),
});

export const markersRouteTree = markersRoute.addChildren([
  markersIndexRoute,
  markersMeasuringMethodsRoute,
  markersMeasuringMethodRequestsRoute,
  markersMeasurementSubmissionsRoute,
]);
